import type { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { isStaff, useSession } from "./auth/session";
import { ErrorBox, Spinner } from "./components/ui";
import { t } from "./i18n";
import { Entrar } from "./pages/Entrar";
import { CriarConta } from "./pages/CriarConta";
import { Cadastro } from "./pages/Cadastro";
import { Painel } from "./pages/Painel";
import { NovoPagamento } from "./pages/NovoPagamento";
import { Pedido } from "./pages/Pedido";
import { Fornecedores } from "./pages/Fornecedores";
import { Documentos } from "./pages/Documentos";
import { Configuracoes } from "./pages/Configuracoes";
import { TocaFila } from "./pages/toca/Fila";
import { TocaKyb } from "./pages/toca/Kyb";
import { TocaAcessos } from "./pages/toca/Acessos";
import { TocaFornecedores } from "./pages/toca/Fornecedores";
import { TocaPedidos } from "./pages/toca/Pedidos";
import { TocaPedido } from "./pages/toca/PedidoDetalhe";
import { TocaReconciliacao } from "./pages/toca/Reconciliacao";
import { TocaConfiguracoes } from "./pages/toca/Configuracoes";
import { TocaAuditoria } from "./pages/toca/Auditoria";
import { Legal } from "./pages/Legal";
import { Pagamentos } from "./pages/Pagamentos";
import { Notificacoes } from "./pages/Notificacoes";
import { EsqueciSenha, RedefinirSenha, Recuperar2fa, Redefinir2fa } from "./pages/Recuperacao";

function FullScreen({ children }: { children: ReactNode }) {
  return (
    <div className="auth-wrap">
      <div style={{ width: 480, maxWidth: "100%" }}>{children}</div>
    </div>
  );
}

/** Exige sessão. */
function RequireAuth({ children }: { children: ReactNode }) {
  const { me, loading, error, refresh } = useSession();
  const loc = useLocation();
  if (loading) return <FullScreen><Spinner /></FullScreen>;
  if (error) return <FullScreen><ErrorBox error={error} onRetry={() => void refresh()} /></FullScreen>;
  if (!me) return <Navigate to="/entrar" replace state={{ from: loc.pathname }} />;
  return <>{children}</>;
}

/** Área do cliente: exige empresa aprovada; senão manda para o cadastro. Staff sem empresa vai para a Toca. */
function RequireApproved({ children }: { children: ReactNode }) {
  const { me } = useSession();
  if (!me) return null;
  if (!me.company) return <Navigate to={isStaff(me) ? "/toca" : "/cadastro"} replace />;
  if (me.company.status !== "approved") return <Navigate to="/cadastro" replace />;
  return <>{children}</>;
}

function RequireStaff({ children }: { children: ReactNode }) {
  const { me } = useSession();
  if (!me) return null;
  if (!isStaff(me)) {
    return (
      <FullScreen>
        <div className="card pad stack">
          <h2>{t.nav.toca}</h2>
          <p className="ink2">{t.toca.notStaff}</p>
          <a className="btn ghost" href="/">{t.nav.goToPanel}</a>
        </div>
      </FullScreen>
    );
  }
  return <>{children}</>;
}

/** Já logado tentando entrar: manda para o lugar certo. */
function PublicOnly({ children }: { children: ReactNode }) {
  const { me, loading } = useSession();
  if (loading) return <FullScreen><Spinner /></FullScreen>;
  if (me) return <Navigate to={homeFor(me)} replace />;
  return <>{children}</>;
}

export function homeFor(me: { user: { staffRole?: string | null }; company?: { status: string } | null }): string {
  if (me.company) return me.company.status === "approved" ? "/" : "/cadastro";
  return me.user.staffRole ? "/toca" : "/cadastro";
}

export function App() {
  return (
    <Routes>
      <Route path="/entrar" element={<PublicOnly><Entrar /></PublicOnly>} />
      <Route path="/criar-conta" element={<PublicOnly><CriarConta /></PublicOnly>} />
      <Route path="/esqueci-senha" element={<PublicOnly><EsqueciSenha /></PublicOnly>} />
      <Route path="/redefinir-senha" element={<RedefinirSenha />} />
      <Route path="/recuperar-2fa" element={<PublicOnly><Recuperar2fa /></PublicOnly>} />
      <Route path="/redefinir-2fa" element={<Redefinir2fa />} />
      <Route path="/termos" element={<Legal doc="termos" />} />
      <Route path="/privacidade" element={<Legal doc="privacidade" />} />

      <Route path="/cadastro" element={<RequireAuth><Cadastro /></RequireAuth>} />

      <Route path="/" element={<RequireAuth><RequireApproved><Painel /></RequireApproved></RequireAuth>} />
      <Route path="/pagamentos" element={<RequireAuth><RequireApproved><Pagamentos /></RequireApproved></RequireAuth>} />
      <Route path="/notificacoes" element={<RequireAuth><RequireApproved><Notificacoes /></RequireApproved></RequireAuth>} />
      <Route path="/pagamentos/novo" element={<RequireAuth><RequireApproved><NovoPagamento /></RequireApproved></RequireAuth>} />
      <Route path="/pagamentos/:id" element={<RequireAuth><RequireApproved><Pedido /></RequireApproved></RequireAuth>} />
      <Route path="/fornecedores" element={<RequireAuth><RequireApproved><Fornecedores /></RequireApproved></RequireAuth>} />
      <Route path="/fornecedores/novo" element={<RequireAuth><RequireApproved><Fornecedores openNew /></RequireApproved></RequireAuth>} />
      <Route path="/documentos" element={<RequireAuth><RequireApproved><Documentos /></RequireApproved></RequireAuth>} />
      <Route path="/configuracoes" element={<RequireAuth><Configuracoes /></RequireAuth>} />

      <Route path="/toca" element={<RequireAuth><RequireStaff><TocaFila /></RequireStaff></RequireAuth>} />
      <Route path="/toca/kyb" element={<RequireAuth><RequireStaff><TocaKyb /></RequireStaff></RequireAuth>} />
      <Route path="/toca/acessos" element={<RequireAuth><RequireStaff><TocaAcessos /></RequireStaff></RequireAuth>} />
      <Route path="/toca/fornecedores" element={<RequireAuth><RequireStaff><TocaFornecedores /></RequireStaff></RequireAuth>} />
      <Route path="/toca/pedidos" element={<RequireAuth><RequireStaff><TocaPedidos /></RequireStaff></RequireAuth>} />
      <Route path="/toca/pedidos/:id" element={<RequireAuth><RequireStaff><TocaPedido /></RequireStaff></RequireAuth>} />
      <Route path="/toca/reconciliacao" element={<RequireAuth><RequireStaff><TocaReconciliacao /></RequireStaff></RequireAuth>} />
      <Route path="/toca/configuracoes" element={<RequireAuth><RequireStaff><TocaConfiguracoes /></RequireStaff></RequireAuth>} />
      <Route path="/toca/auditoria" element={<RequireAuth><RequireStaff><TocaAuditoria /></RequireStaff></RequireAuth>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
